import { config, storage } from "../core/config";
import type { Credential, Envelope } from "../domain/types";
export class ApiError extends Error {
  constructor(
    public code: string,
    public status = 0,
  ) {
    super(code);
    this.name = "ApiError";
  }
}
export function validateEnvelope(body: Envelope, feedback = false) {
  const bad = body.messages?.find((x) => x.type.toUpperCase() === "ERROR");
  if (bad) throw new ApiError(bad.code || "generic", body.status || 200);
  if (body.status && (body.status < 200 || body.status >= 300))
    throw new ApiError("server", body.status);
  if (
    feedback &&
    (body.status !== 200 ||
      !body.messages?.some((x) =>
        ["SUCCESS", "OK"].includes(x.type.toUpperCase()),
      ))
  )
    throw new ApiError("generic");
}
export function canRetry(method: string, status: number) {
  return (
    ["GET", "PUT", "PATCH", "DELETE"].includes(method) &&
    (status === 0 ||
      status === 429 ||
      (status >= 500 && status !== 501 && status !== 505))
  );
}
// Validate the canonical destination before attaching credentials or proxying.
export function requestUrl(
  path: string,
  apiUrl = config.apiUrl,
  localProxy = config.localApiProxy,
  hostname = window.location.hostname,
) {
  const url = new URL(path, apiUrl);
  if (url.origin !== new URL(apiUrl).origin || url.username || url.password)
    throw new ApiError("configuration");
  if (!localProxy) return url;
  if (
    !["127.0.0.1", "localhost"].includes(hostname) ||
    url.origin !== "https://api.filazero.net" ||
    !/^\/(api|v2)\//.test(url.pathname)
  )
    throw new ApiError("configuration");
  return `/__filazero_api${url.pathname}${url.search}`;
}
export async function request<T>(
  path: string,
  method = "GET",
  body?: unknown,
  timeoutMs = 20000,
): Promise<T> {
  const url = requestUrl(path);
  for (let attempt = 0; attempt < 3; attempt++) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const token = storage.get<Credential>("credential")?.access_token;
      const res = await fetch(url, {
        method,
        body: body === undefined ? undefined : JSON.stringify(body),
        headers: {
          ...(body === undefined ? {} : { "Content-Type": "application/json" }),
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",
        signal: controller.signal,
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        if (res.status === 401)
          window.dispatchEvent(new Event("fz:unauthorized"));
        throw new ApiError(
          data?.messages?.find((m: { type: string }) => m.type === "ERROR")
            ?.code || (res.status === 401 ? "sessionExpired" : "server"),
          res.status,
        );
      }
      return data as T;
    } catch (e) {
      const error =
        e instanceof ApiError
          ? e
          : new ApiError(controller.signal.aborted ? "timeout" : "network");
      if (attempt < 2 && canRetry(method, error.status)) {
        await new Promise((r) => setTimeout(r, 2 ** (attempt + 1) * 1000));
        continue;
      }
      throw error;
    } finally {
      clearTimeout(timer);
    }
  }
  throw new ApiError("network");
}
